import qs from 'qs'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { VideoApi } from 'src/api'
import { axiosHiringApi } from 'src/api/axios'
import { Spinner } from 'src/components'
import { EVideoPrivacy } from 'src/enums'
import { useBehaviorMapper, useDebouncedLoading } from 'src/hooks'
import { IconVideoIllustration } from 'src/icons'
import { IVideoModel } from 'src/interfaces'
import { VVCCard } from 'src/partials/card/vvc-card'
import { LoadMoreService } from 'src/services'

interface IProps {
  keyword?: string
  screen?: string
  onUpdateProfile?: (videoId: number, fnCallback?: () => void) => void
}

export const VideosArchive: FC<IProps> = ({ keyword, screen, onUpdateProfile }) => {
  const _loadMoreService = useMemo(() => new LoadMoreService<IVideoModel & { usedForCampaigns?: number }>({
    axiosInstance: axiosHiringApi,
    endpoint: VideoApi._prefix + '?' + qs.stringify({
      keyword,
      privacy: EVideoPrivacy.ARCHIVED
    })
  }), [keyword])

  const { ref: refLoadMore, inView } = useInView({ threshold: 0 })
  const loading = useBehaviorMapper(_loadMoreService.loading$)
  const items = useBehaviorMapper(_loadMoreService.items$)

  const onPublishVideo = useCallback((pfvId: number) => {
    onUpdateProfile?.(pfvId, () => {
      _loadMoreService.refresh()
    })
  }, [onUpdateProfile, _loadMoreService])

  useEffect(() => {
    if (inView && !loading && _loadMoreService.hasMore) {
      _loadMoreService.loadMore()
    }
  }, [_loadMoreService, inView, loading])

  useEffect(() => {
    _loadMoreService.loadMore()
  }, [_loadMoreService])

  const debounceLoading = useDebouncedLoading(!items.length && loading)

  if (debounceLoading) {
    return (
      <div className="fx-1 fx fx-center">
        <Spinner/>
      </div>
    )
  }

  if (!items.length) {
    return (
      <div className="fx-1 fx-column fx-center gap-10">
        <IconVideoIllustration/>

        <div className="fx-column fx-ai-center gap-5">
          <div className="bold-24 fw-700 txt-center txt-black-01">
            Nothing Here Yet
          </div>
          <div className="body-14 txt-black-02 txt-center" style={{ maxWidth: 430 }}>
            This is where your archived videos will appear. Once you archive a video, it'll be stored safely here for future reference.
          </div>
        </div>
      </div>
    )
  }

  return (
    <section className="fx fx-wrap-wrap gap-4">
      {items?.map(item => (
        <VVCCard
          key={item.id}
          video={item}
          screen={screen}
          onChange={(values) => item.id && _loadMoreService.updateOneItem(item.id, values)}
          onPublishVideo={onPublishVideo}
        />
      ))}
      <div ref={refLoadMore}/>
    </section>
  )
}
